// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { isDriversLicense, isExtendedId, isPassport, isRemoteDriversLicense } from '../utils/document-utils';
import { UserTypes } from './user.interfaces';

export enum DdjDocumentRequirementFields {
    mandatoryDocument = 'mandatoryDocument',
    isDealerOnly = 'isDealerOnly',
    needsBuyerSignature = 'needsBuyerSignature',
    needsBuyerUpload = 'needsBuyerUpload',
    needsCoBuyerSignature = 'needsCoBuyerSignature',
    needsCoBuyerUpload = 'needsCoBuyerUpload',
    needsDealerSignature = 'needsDealerSignature',
    needsDealerUpload = 'needsDealerUpload',
    needsDealerVerification = 'needsDealerVerification',
}

export type DdjDocumentRequirements = {
    [key in DdjDocumentRequirementFields]: boolean;
};

export interface DdjDocumentStatuses {
    buyerSignedBy: number;
    buyerSignedDate: string;
    buyerSignedDeviceId: string;
    buyerSignedIpAddress: string;
    coBuyerSignedBy: number;
    coBuyerSignedDate: string;
    coBuyerSignedDeviceId: string;
    coBuyerSignedIpAddress: string;
    dealerVerifiedBy: number;
    dealerVerifiedDate: string;
    dealerVerifiedDeviceId: string;
    dealerVerifiedIpAddress: string;
    dealerSignedBy: number;
    dealerSignedDate: string;
    dealerSignedDeviceId: string;
    dealerSignedIpAddress: string;
    signedByRequest: Assignees;
}

export interface DocumentWithInternalType {
    internalDocumentTypeId?: InternalDocumentTypeIds;
}

export interface DdjDocumentCore {
    documentId?: number;
    dealId?: number;
    displayName: string;
    commonDocument?: boolean;
    systemDocument?: boolean;
    internalDocumentTypeId: InternalDocumentTypeIds;
    uploadTypeId: FormDocumentUploadType;
}

export interface DdjDocumentBasic extends DdjDocumentCore {
    documentStatusId: DocumentStatus;
    isClosed: boolean;
    readyForSignatures: boolean;
    systemDocument: boolean;
}

export interface DdjDocument {
    documentId?: number;
    dealId: number;
    displayName: string;
    preamble: string;
    dealerId?: number;
    internalDocumentName?: string;
    internalDocumentTypeId?: InternalDocumentTypeIds;
    inputFieldSettings?: { [key: string]: PdfField };
    fileName?: string;
    checksum?: number;
    size?: number;
    requirements?: DdjDocumentRequirements;
    status?: DdjDocumentStatuses;
    documentStatusId: DocumentStatus;
    documentCreatedBy?: number;
    documentCreateDate?: string;
    documentLastUpdateBy?: number;
    documentLastUpdateDate?: string;
    isClosed: boolean;
    isDealerOnly: boolean;
    readyForSignatures: boolean;
    uploadTypeId: FormDocumentUploadType;
    offsetX: number;
    offsetY: number;
    //Required on all deals. Only DDJ Admin should be able to change.
    systemDocument: boolean;
    templateDocumentId: number;
    checked?: boolean;
    documentSignedURL: string;
    signedPdfFields?: [{ [key: string]: PdfField }];
    commonDocumentState?: string;
    commonDocumentStateDisplay?: string;
    commonDocumentType?: CommonDocumentType;
    vendorPartyDocumentFile?: any;
    isNotarize?: boolean;
}

export interface DdjTemplate extends DdjDocument {
    templateId: number;
    deal: {
        mandatoryDocument: boolean;
        isNew: boolean;
        isUsed: boolean;
        isBuy: boolean;
        isLease: boolean;
        isCash: boolean;
        isFinance: boolean;
        isTradeIn: boolean;
        isLeaseTurnIn: boolean;
    };
}

export interface PddjQueue {
    type: string;
    mimeType?: string;
    name: string;
    blob: Blob;
    impact: ImpactPrinterFields;
}

export enum Assignees {
    unk = '',
    dealer = 'dealer',
    buyer = 'buyer',
    cobuyer = 'cobuyer',
    //type is for system data to document fields
    deal = 'deal',
}

export enum CommonDocumentType {
    state = 1,
    nada = 2,
    universal = 3,
}

export enum FieldTypes {
    signature = 'SIGNATURE',
    initials = 'INITIALS',
    legalName = 'LEGAL_NAME',
    date = 'DATE',
    text = 'TEXT',
    optionalText = 'OPTIONAL',
    requiredText = 'REQUIRED',
    vin = 'VIN',
    last8Vin = 'LAST_8_VIN',
    vehicleYear = 'VEHICLE_YEAR',
    vehicleMake = 'VEHICLE_MAKE',
    vehicleModel = 'VEHICLE_MODEL',
    vehicleTrim = 'VEHICLE_TRIM',
    tradeInVehicleYear = 'TRADE_IN_VEHICLE_YEAR',
    tradeInVehicleMake = 'TRADE_IN_VEHICLE_MAKE',
    tradeInVehicleModel = 'TRADE_IN_VEHICLE_MODEL',
    tradeInVehicleTrim = 'TRADE_IN_VEHICLE_TRIM',
    tradeInVehiclePayoff = 'TRADE_IN_PAYOFF',
    stockId = 'STOCK_ID',
    mileage = 'MILEAGE',
    jobTitle = 'JOB_TITLE',
    tradeInVin = 'TRADE_IN_VIN',
    dealNumber = 'DEAL_NUMBER',
    buyerName = 'BUYER_NAME',
    cobuyerName = 'COBUYER_NAME',
    dealershipName = 'STORE_NAME',
    listOfDocuments = 'LIST_OF_DEAL_DOCUMENTS',
    dealerContact = 'FandI_Manager',
    optionalSignature = 'OPTIONAL_SIGNATURE',
    optionalInitials = 'OPTIONAL_INITIALS',
    optionalLegalName = 'OPTIONAL_LEGAL_NAME',
    invalid = '',
}

export interface PdfField {
    assignee: Assignees;
    fontSize: number;
    height: number;
    name: string;
    order: number;
    page: number;
    signedAt: Date;
    signedBy: string;
    thirdPartyName?: string;
    thirdPartyType?: ThirdPartyType;
    type: FieldTypes;
    width: number;
    value: any;
    x: number;
    y: number;
    setAsActive: boolean;
}

export interface CustomAnnotation {
    assignee: Assignees;
    flag?: any;
    name: string;
    type: FieldTypes;
    value: any;
}
export interface ImpactPrinterFields {
    raw?: string;
    rawBase64?: string;
    DocumentData?: DocumentData;
    DocumentDataLines?: [DocumentData];
    MetaDataPrinter?: ImpactPrinterMetaData;
    QueueName?: string;
}

export interface DocumentData {
    LineData: [LineDetail];
    CustomVerticalSpacing: string;
    LineFeedVerticalSpacing: string;
    PreviousCustomVerticalSpacing: string;
}

export interface LineDetail {
    Spacing: string;
    Content: string;
}

export interface ImpactPrinterMetaData {
    PaperSize: string;
    FontType: string;
    FontSize: string;
    AppVersion: string;
    Driver: string;
    advancedPageSettings?: ImpactPageSettings;
}

export interface ImpactPageSettings {
    PageHeight: number;
    PageWidth: number;
    NumberOfPages: number;
    PageBoundryX: number;
    PageBoundryX2: number;
    PageBoundryY: number;
    PageBoundryY2: number;
}

export interface TemplateUploadType {
    uploadTypeId: FormDocumentUploadType;
    uploadTypeDescription: string;
    dataLoadFlag: boolean;
    hint: string;
    value: string;
}

export enum DocumentStatus {
    Needed = 1,
    Draft = 2,
    BuyerQueue = 3,
    CoBuyerQueue = 4,
    DealerQueue = 5,
    Complete = 6,
    Rejected = 7,
    Cancelled = 8,
    Deleted = 9,
    DataOverlayNeeded = 10,
    WaitingOnBuyerESignConstent = 12,
    WaitingOnCoBuyerESignConstent = 13,
    ReviewQueue = 15,
    EnqueueCertification = 18,
    InterventionRequired = 20,
    NotarizeQueue = 21,
}

export enum DdjRouteOneErrorCodes {
    NoError = 0,
    SigningError = 10,
    ContractError = 20,
    UnknownError = 100,
    EnrolledHasBadCreds = 200,
    EnrolledMissingDealerId = 201,
    NotEnrolled = 300,
    NotActiviated = 301,
    Terminated = 400,
    BadAuditCreds = 500,
}

export enum FormDocumentUploadType {
    Predefined_Form = 1,
    Uploaded_During_Deal = 2,
    Print_From_PDF = 3,
    Print_From_Impact_Form = 4,
    Manually_Filled_During_Deal = 5,
    Dealer_Uploaded_with_Predefine_Signatures = 6,
    Consumer_Documents = 7,
}

export enum AssetType {
    All = 0,
    PrintToDDJ = 1,
}

export interface InternalDocumentType {
    internalDocumentTypeId: InternalDocumentTypeIds;
    internalDocumentName: string;
    active: boolean;
    suggestedPreamble: string;
    isDealerOnly: boolean;
    autoLoadDoc: boolean;
    ddjManaged: boolean;
}

export enum InternalDocumentTypeIds {
    ApplicationForStateTitleAndRegistration = 1,
    /** @Deprecated **/
    BuyerDriverLicenseBack = 2,
    /** @Deprecated **/
    BuyerDriverLicenseFront = 3,
    CarFax = 4,
    /** @Deprecated **/
    CoBuyerDriverLicenseBack = 5,
    /** @Deprecated **/
    CoBuyerDriverLicenseFront = 6,
    ProofOfInsurance = 7,
    TruthInLendingDisclosure = 8,
    MonroneyLabel = 10,
    ErrorsAndOmissions = 11,
    AgreementToFurnishInsurement = 12,
    CustomerWelcomeCall = 13,
    DealerMaintenanceCustomerApplication = 14,
    CPOLimitedWarranty = 15,
    NoticeToCoSigner = 16,
    OdometerDisclosureStatement = 17,
    ApplicationForVehicleTitle = 18,
    SpecialMailingOfCertificateOfTitle = 19,
    AppointmentOfAgent = 20,
    NoticeOfAssignmentOfSecuredInterestInAVehicleTr210 = 21,
    VehiclePurchaseAgreement = 22,
    CustomerSurvey = 23,
    BuyerESignConsent = 24,
    BuyerFinalSignatureDocument = 25,
    Other = 26, //anything not specifically defined (user-created, not yet DDJ-standard)
    WeOwe = 27,
    PrivacyNotice = 28,
    RebateForm = 29,
    ConquestForm = 30,
    LoyaltyForm = 31,
    VipForm = 32,
    CobuyerESignConsent = 33,
    CoBuyerFinalSignatureDocument = 34,
    Lease = 35,
    CreditLife = 36,
    Paystub1 = 37,
    Paystub2 = 38,
    UtilityBill = 39,
    BankStatement = 40,
    VehicleRegistation = 41,
    IdVerificationResults = 42,
    BuyerDriversLicense = 43,
    CoBuyerDriversLicense = 44,
    BuyerDriversLicenseRemoteUpload = 45,
    CoBuyerDriversLicenseRemoteUpload = 46,
    BuyerPassportBooklet = 47,
    CoBuyerPassportBooklet = 48,
    BuyerPassportCard = 49,
    CoBuyerPassportCard = 50,
    BuyerInternational_Id_Dl = 51,
    CobuyerInternational_Id_Dl = 52,
}

export interface TemplateGroup {
    groupName: string;
    templates: number[];
    templateGroupId: number;
}

export interface TemplateState {
    stateCode: string;
    stateName: string;
    expanded?: boolean;
}

export interface SelectedTemplateGroup {
    templateGroupId: number;
    dealerId: number;
}

export interface SystemDocument {
    currentSystemDocumentId: number;
    htmlDoc?: string;
    pdfUrl?: string;
    systemDocumentDescription: string;
    systemDocumentTypeId: number;
    userId: number;
    accepted?: boolean;
}

export interface SystemMessage {
    messageTitle: string;
    messageHTML: string;
    systemMessageColor: string;
}

export interface DocumentQueueUpdate {
    dealId: number;
    documentId: number;
    documentStatusId: DocumentStatus;
}

export interface DealDocuments {
    complete: DdjDocument[];
    buyer: DdjDocument[];
    cobuyer: DdjDocument[];
    dealer: DdjDocument[];
    signingCertInProcess: DdjDocument[];
    interventionRequired: DdjDocument[];
    deleted: DdjDocument[];
    notarizeQueue: DdjDocument[];
}

export enum PddjPrinters {
    Pdf = 'Print to DDJ',
    GenericImpact = 'Print to DDJ Impact Printer',
    Oki8480fbImpact = 'Print to DDJ Impact Oki 8480FB',
}

export interface HashCompareRequest {
    dealId: number;
    dealerId: number;
    documentId: number;
    hash: string;
    hashType: string;
}

export interface GuidedSigningState {
    nextField: any;
    fieldsToComplete: number;
    emptyOptionalChecks: number;
}

export interface StandardDocument {
    standardDocumentId: number;
    standardDocumentName: string;
    internalDocumentTypeId?: InternalDocumentTypeIds;
    dealerId?: number;
    orderByNum: number;
    createdBy?: number;
    createDate?: Date;
    lastUpdateBy?: number;
    lastUpdateDate?: Date;
    isDisabled?: boolean;
    disabledMessage?: string;
}

export enum SystemDocumentType {
    TOS = 1,
    PrivacyPolicy = 2,
    ReleaseNotes = 3,
    DPA = 4,
}

export class IdVerification {
    documentId: number;
    internalDocumentTypeId: InternalDocumentTypeIds;
    resultsDocumentId: number;
    verified: boolean;
    hasWarnings: boolean;
    expired: boolean;
    verificationStatus: IdCheckStatus;

    constructor(data: object) {
        Object.assign(this, data);
    }

    //Helper methods for angular templates
    public get status(): IdCheckStatus {
        return this.verificationStatus;
    }

    public get statusDescription(): string {
        switch (this.status) {
            case IdCheckStatus.Pending:
                return 'Pending';
            case IdCheckStatus.Passed:
                return 'Valid';
            case IdCheckStatus.NameOnDealMismatch:
            case IdCheckStatus.PassedWithWarnings:
            case IdCheckStatus.FrontFailedBackPassed:
            case IdCheckStatus.FacialRecognitionFailed:
                return 'Valid (Warning)';
            case IdCheckStatus.FrontPassedBackFailed:
                return 'Not Verified';
            case IdCheckStatus.Failed:
            case IdCheckStatus.ParsedOkButInvalid:
                return 'Invalid';
            case IdCheckStatus.Expired:
                return 'Expired';
            case IdCheckStatus.ReadyForSubmit:
                return 'Need to Resubmit';
            default:
                return null;
        }
    }

    public get expiredStatus(): boolean {
        return this.status === IdCheckStatus.Expired;
    }

    public get passed(): boolean {
        return this.status === IdCheckStatus.Passed;
    }

    public get passedWithWarnings(): boolean {
        return this.status === IdCheckStatus.NameOnDealMismatch || this.status === IdCheckStatus.PassedWithWarnings;
    }

    public get pending(): boolean {
        return this.status === IdCheckStatus.Pending;
    }

    public get failed(): boolean {
        return this.status === IdCheckStatus.Failed;
    }

    public get unprocessed(): boolean {
        return !this.status;
    }

    //Used for displaying the document name in the customer info section (doesn't have buyer/cobuyer indicator)
    public get shortDisplayName(): string {
        if (isDriversLicense(this.internalDocumentTypeId)) {
            if (isRemoteDriversLicense(this.internalDocumentTypeId)) {
                return 'ID Check (Remote Upload)';
            }
            return 'ID Check';
        } else if (isPassport(this.internalDocumentTypeId)) {
            return 'Passport';
        } else if (isExtendedId(this.internalDocumentTypeId)) {
            return 'ID Check';
        }
        throw new Error('Invalid identification document type');
    }
}
export class IdVerificationUtilizationModel {
    dealerId: number;
    oemCode: string;
    oemDealerId: number;
    productId: number;
    monthOf: Date;
    numberOfScans: number;
    activationDate: Date;
    turnOffAccessDate: Date;
    currentlyActive: boolean;
    numberOfScansAllowed: number;
    overrideLimit?: number;
    oemName: string;

    //Helper methods for angular templates
}

export class AssigneeResolver {
    private userType: string;
    private userId: string;
    private userEmail: string;
    private userDealerId: string;
    private dealSalesPersonEmail: string;
    private dealDealerId: string;
    private dealBuyerUserId: string;
    private dealCoBuyerId: string;

    constructor(
        userType: string,
        userId: string,
        userEmail: string,
        userDealerId: string,
        dealSalesPersonEmail: string,
        dealDealerId: string,
        dealBuyerUserId: string,
        dealCoBuyerId: string
    ) {
        this.userType = userType;
        this.userId = userId;
        this.userEmail = userEmail;
        this.userDealerId = userDealerId;
        this.dealSalesPersonEmail = dealSalesPersonEmail;
        this.dealDealerId = dealDealerId;
        this.dealBuyerUserId = dealBuyerUserId;
        this.dealCoBuyerId = dealCoBuyerId;
    }

    public get assigneeType(): Assignees {
        // Allow the salesperson or anyone else at the dealership to sign for the dealer
        if (
            this.userEmail === this.dealSalesPersonEmail ||
            (this.userDealerId === this.dealDealerId && (this.userType == UserTypes.Dealer.toString() || this.userType == UserTypes.Internal.toString()))
        ) {
            return Assignees.dealer;
        }
        if (this.userId === this.dealBuyerUserId) {
            return Assignees.buyer;
        } else if (this.userId === this.dealCoBuyerId) {
            return Assignees.cobuyer;
        } else {
            // If we receive undefined from this function
            // assume the user has no signing priviledges
            console.error('No Assignee Found');
            return undefined;
        }
    }
}

export enum ProductIds {
    EzDocsBase = 1,
    EzDocsAutomate = 2,
    Notarize = 3,
    DriversLicenseScan = 4,
    Training = 5,
    RouteOne = 6,
}

export enum IdCheckStatus {
    Pending = 1, // gray
    Passed = 2, // green
    NameOnDealMismatch = 3, // yellow
    Failed = 4, // red
    Expired = 5, // red
    PassedWithWarnings = 6, // yellow
    FrontFailedBackPassed = 7, // yellow
    ReadyForSubmit = 8, // orange
    FacialRecognitionFailed = 9, // yellow
    FrontPassedBackFailed = 10, // grey
    ParsedOkButInvalid = 11, // red (assumed to be fake)
}

export interface IntellicheckIdUpload {
    documentId: number;
    requestIdentifier: string;
    token?: string;
    payload: IntellicheckPayload;
}

export interface IntellicheckPayload {
    backData?: string;
    frontData?: string;
    barcodeData?: string;
    selfieImage?: string;
    requestingFacialRecognition: boolean;
}

export enum ThirdPartyType {
    ROUTE_ONE = 1,
    DEALER_TRACK = 2,
}

import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
@Injectable({
    providedIn: 'root',
})
export class DateTimeDoc {
    constructor(readonly datePipe: DatePipe) {}
    //All Document Dates should be UTC Time.
    public get formatToUTC(): string {
        const currentDate = new Date();
        return this.datePipe.transform(
            new Date(
                currentDate.getUTCFullYear(),
                currentDate.getUTCMonth(),
                currentDate.getUTCDate(),
                currentDate.getUTCHours(),
                currentDate.getUTCMinutes(),
                currentDate.getUTCSeconds()
            ),
            'yyyy-MM-dd HH:mm:ss'
        );
    }
}
